import { useRef, useState } from 'react'
import Image from 'next/image'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'

const getCarouselPageIndex = (swiper, direction) => {
  const { activeIndex, slidesGrid, width } = swiper
  const currentPosition = slidesGrid[activeIndex]

  if (direction === 'previous') {
    const targetPosition = Math.max(currentPosition - width, 0)
    for (let index = activeIndex - 1; index >= 0; index -= 1) {
      if (slidesGrid[index] <= targetPosition) return index
    }
    return 0
  }

  const targetPosition = currentPosition + width
  const nextIndex = slidesGrid.findIndex(
    (position, index) => index > activeIndex && position >= targetPosition
  )
  return nextIndex === -1 ? slidesGrid.length - 1 : nextIndex
}

const VenderCertification = ({ sinyiLivingCertification }) => {
  const items = sinyiLivingCertification?.items || []
  const swiperRef = useRef(null)
  const [isAtStart, setIsAtStart] = useState(true)
  const [isAtEnd, setIsAtEnd] = useState(items.length <= 3)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const updateNavigationState = (swiper) => {
    setIsAtStart(swiper.isBeginning)
    setIsAtEnd(swiper.isEnd)
  }

  if (!sinyiLivingCertification?.isVisible || !items.length) return null

  return (
    <Box mb={18}>
      <Box>
        <Box
          mb={4.5}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h5">信義居家認證合作廠商</Typography>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            {items.length > 3 && !isMobile && (
              <Box display="flex" gap={3}>
                <IconButton
                  aria-label="上一排信義居家認證標章"
                  onClick={() => {
                    const swiper = swiperRef.current
                    if (!swiper) return
                    swiper.slideTo(getCarouselPageIndex(swiper, 'previous'))
                  }}
                  disabled={isAtStart}
                  size="small"
                  sx={{ borderRadius: '24px' }}
                >
                  <ChevronLeftIcon />
                </IconButton>
                <IconButton
                  aria-label="下一排信義居家認證標章"
                  onClick={() => {
                    const swiper = swiperRef.current
                    if (!swiper) return
                    swiper.slideTo(getCarouselPageIndex(swiper, 'next'))
                  }}
                  disabled={isAtEnd}
                  size="small"
                  sx={{ borderRadius: '24px' }}
                >
                  <ChevronRightIcon />
                </IconButton>
              </Box>
            )}
          </Box>
        </Box>
        <Typography
          variant="paraBodyMedium"
          component="div"
          color="text.minus2"
        >
          本廠商通過信義居家合作審核機制，具備
          <Typography
            component="span"
            variant="paraBodyMedium"
            color="primary.main"
          >
            合法登記、專業資格與良好商譽
          </Typography>
          ，並經
          <Typography
            component="span"
            variant="paraBodyMedium"
            color="primary.main"
          >
            每年定期審查
          </Typography>
          ，持續符合服務品質標準。審查通過者將獲頒當年度「信義居家認證合作廠商標章」，一眼辨識、安心選擇。
        </Typography>
      </Box>

      <Box mr={{ xs: -4, lg: -9, xl: -12, xxl: -16 }} overflow="hidden">
        <Swiper
          slidesPerView="auto"
          spaceBetween={16}
          slidesOffsetAfter={0}
          threshold={8}
          grabCursor
          simulateTouch
          allowTouchMove
          watchOverflow
          breakpoints={{
            768: {
              spaceBetween: 16,
              slidesOffsetAfter: 16,
            },
            992: {
              spaceBetween: 16,
              slidesOffsetAfter: 36,
            },
            1200: {
              spaceBetween: 24,
              slidesOffsetAfter: 48,
            },
            1600: {
              spaceBetween: 32,
              slidesOffsetAfter: 64,
            },
          }}
          onSwiper={(swiper) => {
            swiperRef.current = swiper
            updateNavigationState(swiper)
          }}
          onSlideChange={updateNavigationState}
          onReachBeginning={updateNavigationState}
          onReachEnd={updateNavigationState}
          onFromEdge={updateNavigationState}
          onResize={updateNavigationState}
          style={{ overflow: 'visible' }}
        >
          {items.map((item, index) => (
            <SwiperSlide key={index} style={{ width: 'auto' }}>
              <Box
                width={{
                  xs: '70vw',
                  sm: '36vw',
                  md: '25vw',
                  xxl: '400px',
                }}
              >
                <Box p={3} borderRadius="16px" sx={{ aspectRatio: '1 / 1' }}>
                  <Box
                    position="relative"
                    width="100%"
                    height="100%"
                    borderRadius="12px"
                    overflow="hidden"
                  >
                    {item?.image?.url && (
                      <Image
                        src={item.image.url}
                        fill
                        draggable={false}
                        sizes="(max-width: 575px) 70vw, (max-width: 767px) 36vw, (max-width: 1600px) 25vw, 400px"
                        style={{ objectFit: 'contain' }}
                        alt={item.alt || ''}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </SwiperSlide>
          ))}
        </Swiper>
      </Box>
    </Box>
  )
}

export default VenderCertification
